import React, { useMemo, useState } from "react";
import "./MaterialCalculator.css";

/*
 * Calculadora de Material — Decototal
 * Portada desde el proyecto Lovable a React + CSS plano (sin Tailwind, shadcn,
 * sonner ni lucide). Un solo componente + un solo archivo CSS.
 *
 * Lógica de cálculo (idéntica al original):
 *   cantidad = Math.ceil((area * factorMerma) / rendimientoPorUnidad)
 *   factorMerma = 1.05 (Con Merma) | 1.0 (Sin Merma)
 */

// ---------------------------------------------------------------------------
// Datos de producto
// ---------------------------------------------------------------------------
const PRODUCTS = [
  { id: "spc-luxury", name: "Pisos SPC Luxury", category: "Pisos", unit: "Cajas", unitSingular: "caja", coverage: 2.9, note: "Cada caja de SPC Luxury cubre 2.9 m² (13 piezas de 1.22m x 18cm x 4.5mm).", hue: 50 },
  { id: "deck-tablon-wpc", name: "Deck Tablón WPC", category: "Deck", unit: "Piezas", unitSingular: "pieza", coverage: 0.406, note: "Cada pieza de Deck WPC cubre 0.406 m² (2.90m x 14cm x 1.5cm).", hue: 20 },
  { id: "lvt", name: "Piso Vinílico LVT", category: "Pisos", unit: "Cajas", unitSingular: "caja", coverage: 2.4, note: "Cada caja de LVT cubre 2.4 m² (18 piezas de 91cm x 15cm, sin pérdida por empalme).", hue: 60 },
  { id: "piso-deck-sintetico", name: "Piso Deck Sintético", category: "Deck", unit: "Piezas", unitSingular: "pieza", coverage: 0.406, note: "Cada pieza de Piso Deck Sintético cubre 0.406 m² (2.90m x 14cm).", hue: 15 },
  { id: "lambrin-concavo", name: "Muro Lambrín Cóncavo", category: "Muro", unit: "Piezas", unitSingular: "pieza", coverage: 0.551, orientable: true, pieceLength: 2.9, usefulWidth: 0.19, note: "Cada pieza de Lambrín Cóncavo cubre 0.551 m² (2.90m x 19cm, sin pérdida por empalme).", hue: 200 },
  { id: "bamboo-espejo", name: "Muro Bamboo Espejo", category: "Muro", unit: "Piezas", unitSingular: "pieza", coverage: 3.416, orientable: true, pieceLength: 2.8, usefulWidth: 1.22, note: "Cada pieza de Bamboo Espejo cubre 3.416 m² (1.22m x 2.80m, sin reutilizar recortes).", hue: 90 },
  { id: "placa-marmol", name: "Muro Placa Tipo Mármol", category: "Muro", unit: "Placas", unitSingular: "placa", coverage: 2.97, note: "Cada placa tipo mármol cubre 2.97 m² (1.22m x 2.44m).", hue: 240 },
  { id: "lambrin-sintetico-wpc", name: "Muro Lambrín Sintético WPC", category: "Muro", unit: "Piezas", unitSingular: "pieza", coverage: 0.609, orientable: true, pieceLength: 2.9, usefulWidth: 0.21, note: "Nota: Cada pieza de Lambrín WPC (Uso Exterior) cubre 0.609 m² de ancho útil por empalme (2.9m x 21cm útiles; 22cm totales).", hue: 40 },
  { id: "lambrin-sintetico-pvc", name: "Muro Lambrín Sintético PVC", category: "Muro", unit: "Piezas", unitSingular: "pieza", coverage: 0.464, orientable: true, pieceLength: 2.9, usefulWidth: 0.16, note: "Nota: Cada pieza de Lambrín PVC (Uso Interior) cubre 0.464 m² de ancho útil por empalme (2.9m x 16cm útiles; 17cm totales).", hue: 45 },
  { id: "muro-deck-sintetico", name: "Muro Deck Sintético", category: "Muro", unit: "Piezas", unitSingular: "pieza", coverage: 0.362, note: "Nota: Cada pieza de Muro Deck Sintético (Uso Exterior) cubre 0.362 m² (2.90m x 13.5cm x 2cm).", hue: 35 },
  { id: "plafon", name: "Techo Plafón Falso", category: "Techo", unit: "Piezas", unitSingular: "pieza", coverage: 1.4, note: "Cada pieza de Plafón cubre 1.4 m² (5.95m de largo, 4.3kg).", hue: 220 },
];

const WHATSAPP_NUMBER = "525512989224";

// TODO: Reemplaza con tu Portal ID y Form ID reales de HubSpot.
const HUBSPOT_PORTAL_ID = "47654427";
const HUBSPOT_FORM_ID = "0fd3aa49-8045-435d-b22a-c182223105e5";

// Valor interno EXACTO de la opción del dropdown "producto" en HubSpot, por id.
// El texto debe ser idéntico al "Nombre interno" de la opción en HubSpot.
const HUBSPOT_PRODUCT_VALUE = {
  "spc-luxury": "Pisos SPC",
  "deck-tablon-wpc": "Tablón WPC",
  "lvt": "Pisos Vinílicos LVT",
  "piso-deck-sintetico": "Deck Sintético",
  "lambrin-concavo": "Lambrín Cóncavo WPC",
  "bamboo-espejo": "Placa Bamboo Espejo",
  "placa-marmol": "Placa Tipo Mármol",
  "lambrin-sintetico-wpc": "Lambrín de WPC",
  "lambrin-sintetico-pvc": "Lambrín de PVC",
  "muro-deck-sintetico": "Muro Deck Sintético",
  "plafon": "Plafón Falso",
};

// ---------------------------------------------------------------------------
// Imágenes de producto (servidas desde tu propio dominio)
// ---------------------------------------------------------------------------
// Coloca los 13 .webp en:  public/assets/img/calculadora/
// Deben llamarse EXACTAMENTE como abajo (mismos nombres del proyecto Lovable).
// Si una imagen faltara, el componente muestra un degradado de respaldo.
//
// ¿Prefieres volver al CDN de Lovable sin bajar nada? Reemplaza IMG_BASE por:
//   const IMG_BASE = "https://decototal-calculadora.lovable.app/__l5e/assets-v1";
//   y antepón el asset-id a cada archivo (ver README-integracion.md).
const IMG_BASE = process.env.PUBLIC_URL + "/assets/img/calculadora";

const PRODUCT_IMAGES = {
  "spc-luxury": `${IMG_BASE}/piso-spc-luxury.webp`,
  "deck-tablon-wpc": `${IMG_BASE}/tablon-deck-wpc.webp`,
  "lvt": `${IMG_BASE}/piso-lvt.webp`,
  "piso-deck-sintetico": `${IMG_BASE}/piso-wpc-deck.webp`,
  "lambrin-concavo": `${IMG_BASE}/lambrin-concavo.webp`,
  "bamboo-espejo": `${IMG_BASE}/muro-bamboo-espejo.webp`,
  "placa-marmol": `${IMG_BASE}/placa-tipo-rigida-marmol.webp`,
  "lambrin-sintetico-wpc": `${IMG_BASE}/lambrin-muro-wpc.webp`,
  "lambrin-sintetico-pvc": `${IMG_BASE}/lambrin-muro-pvc.webp`,
  "muro-deck-sintetico": `${IMG_BASE}/muro-deck-sintetico.webp`,
  "plafon": `${IMG_BASE}/plafon-falso-pvc.webp`,
};

const CATEGORY_FILTERS = [
  { id: "pisos-decks", label: "Pisos y Decks", matches: ["Pisos", "Deck"] },
  { id: "muros-placas", label: "Muros y Placas", matches: ["Muro"] },
  { id: "techos-estructura", label: "Techos", matches: ["Techo"] },
];

// ---------------------------------------------------------------------------
// Miniatura de producto (con degradado de respaldo si la imagen falla)
// ---------------------------------------------------------------------------
function ProductThumb({ product, size = "md" }) {
  const [failed, setFailed] = useState(false);
  const imageUrl = PRODUCT_IMAGES[product.id];
  const showImage = imageUrl && !failed;
  return (
    <div
      className={`dc-thumb ${size === "sm" ? "dc-thumb--sm" : "dc-thumb--md"}`}
      style={
        showImage
          ? undefined
          : { background: `linear-gradient(135deg, oklch(0.78 0.08 ${product.hue}), oklch(0.55 0.12 ${product.hue}))` }
      }
      aria-hidden="true"
    >
      {showImage ? (
        <img
          src={imageUrl}
          alt={product.name}
          className="dc-thumb-img"
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : (
        <div className="dc-thumb-pattern" />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------
export function MaterialCalculator() {
  const [productId, setProductId] = useState(PRODUCTS[0].id);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [mode, setMode] = useState("area"); // "area" | "dim"
  const [waste, setWaste] = useState("with"); // "with" | "without"
  const [area, setArea] = useState("");
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [wallWidth, setWallWidth] = useState(""); // ancho del muro (solo lambrines/placas)
  const [wallHeight, setWallHeight] = useState(""); // altura del muro (solo lambrines/placas)
  const [leadName, setLeadName] = useState("");
  const [leadPhone, setLeadPhone] = useState("");
  const [formError, setFormError] = useState("");
  const [calcError, setCalcError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const product = useMemo(() => PRODUCTS.find((p) => p.id === productId), [productId]);

  const handleCategoryClick = (catId) => {
    setCategoryFilter((current) => (current === catId ? "" : catId));
  };

  const filteredProducts = useMemo(() => {
    if (!categoryFilter) return PRODUCTS;
    const f = CATEGORY_FILTERS.find((c) => c.id === categoryFilter);
    if (!f) return PRODUCTS;
    return PRODUCTS.filter((p) => f.matches.includes(p.category));
  }, [categoryFilter]);

  const computedArea = useMemo(() => {
    if (mode === "area") return parseFloat(area) || 0;
    const l = parseFloat(length) || 0;
    const w = parseFloat(width) || 0;
    return l * w;
  }, [mode, area, length, width]);

  // Resultado en vivo: se recalcula con cada cambio de input / merma / producto.
  const result = useMemo(() => {
    if (!product) return null;
    const factor = waste === "with" ? 1.05 : 1.0;

    // Lambrines: piezas de 2.90 m que se cortan; el sobrante del largo es merma total.
    // La orientación define qué dimensión del muro consume el largo de la pieza.
    if (product.orientable) {
      const W = parseFloat(wallWidth) || 0;
      const H = parseFloat(wallHeight) || 0;
      if (W <= 0 || H <= 0) return null; // ancho y altura obligatorios
      // Instalación siempre vertical: el ANCHO del muro lo cubre el ancho útil de
      // la pieza, y el LARGO de la pieza recorre la ALTURA (el sobrante es merma).
      const runs = Math.ceil((W * factor) / product.usefulWidth); // columnas a lo ancho
      const perRun = Math.ceil(H / product.pieceLength); // piezas por columna
      const quantity = runs * perRun;
      const acquiredArea = quantity * product.usefulWidth * product.pieceLength;
      return {
        quantity,
        product,
        baseArea: W * H,
        wasteApplied: waste,
        isOrientable: true,
        wallW: W,
        wallH: H,
        runs,
        perRun,
        areaLabel: `Material a adquirir (incluye corte de largo${waste === "with" ? " + 5%" : ""})`,
        areaValue: acquiredArea,
      };
    }

    // Pisos y demás: los recortes se reaprovechan; cálculo por área / rendimiento.
    if (computedArea <= 0) return null;
    const totalWithWaste = computedArea * factor;
    const quantity = Math.ceil(totalWithWaste / product.coverage);
    return {
      quantity,
      product,
      baseArea: computedArea,
      wasteApplied: waste,
      isOrientable: false,
      areaLabel: `Área total requerida ${waste === "with" ? "(con 5% de merma)" : "(medida exacta)"}`,
      areaValue: totalWithWaste,
    };
  }, [product, computedArea, waste, wallWidth, wallHeight]);

  const handleCalculate = () => {
    setCalcError("");
    if (!productId) {
      setCalcError("Por favor, selecciona un producto.");
      return;
    }
    // Lambrines: se piden ancho y altura del muro (sin área total ni largo×ancho).
    if (product?.orientable) {
      if (!wallWidth || parseFloat(wallWidth) <= 0) {
        setCalcError("Por favor, ingresa el ancho del muro en metros.");
        return;
      }
      if (!wallHeight || parseFloat(wallHeight) <= 0) {
        setCalcError("Por favor, ingresa la altura del muro en metros.");
        return;
      }
    } else if (mode === "area") {
      if (!area || parseFloat(area) <= 0) {
        setCalcError("Por favor, ingresa el área total de tu espacio en m².");
        return;
      }
    } else {
      const l = parseFloat(length);
      const w = parseFloat(width);
      if (!length || !width || !(l > 0) || !(w > 0)) {
        setCalcError("Por favor, ingresa el largo y ancho de tu espacio.");
        return;
      }
    }
    setTimeout(() => {
      const el = document.getElementById("result-card");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const whatsappUrl = result
    ? `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
        `Hola Decototal, usé la calculadora de su web. Me interesa cotizar este producto: *${result.product.name}*. Área requerida: *${result.baseArea.toFixed(2)} m²*.${result.isOrientable ? ` Muro: *${result.wallW.toFixed(2)} × ${result.wallH.toFixed(2)} m*.` : ""} El resultado del cálculo indica que necesito adquirir: *${result.quantity} ${result.product.unit}*. Mi nombre es *${leadName || ""}*.`
      )}`
    : "#";

  const handleLeadSubmit = async () => {
    setFormError("");
    if (!result) {
      setFormError("Primero calcula tu material.");
      return;
    }
    const name = leadName.trim();
    const phone = leadPhone.replace(/\D/g, "");
    if (!name) {
      setFormError("Por favor, ingresa tu nombre.");
      return;
    }
    if (phone.length !== 10) {
      setFormError("Por favor, ingresa un número de WhatsApp válido a 10 dígitos.");
      return;
    }

    // Detalle del cálculo que se guarda en el campo "Mensaje" del formulario.
    const detalle =
      `Cotización desde la calculadora. Producto: ${result.product.name}. ` +
      `Área: ${result.baseArea.toFixed(2)} m². Cantidad: ${result.quantity} ${result.product.unit}.` +
      (result.isOrientable
        ? ` Muro: ${result.wallW.toFixed(2)} × ${result.wallH.toFixed(2)} m (instalación vertical).`
        : "");

    setSubmitting(true);
    try {
      const res = await fetch(
        `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_ID}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            // IMPORTANTE: cada "name" debe coincidir EXACTO con el campo del
            // formulario de HubSpot. Enviar campos que no existen en el
            // formulario, u omitir un campo obligatorio, hace que HubSpot
            // rechace TODA la petición (nada se guarda).
            fields: [
              { name: "firstname", value: name },
              { name: "phone", value: phone },
              { name: "producto", value: HUBSPOT_PRODUCT_VALUE[result.product.id] || result.product.name },
              { name: "message", value: detalle },
            ],
            context: {
              pageUri: typeof window !== "undefined" ? window.location.href : "",
              pageName: typeof document !== "undefined" ? document.title : "",
            },
          }),
        }
      );
      if (!res.ok) {
        // HubSpot devuelve el detalle del error aquí (campo faltante, nombre
        // incorrecto, opción de dropdown inválida, etc.). Revísalo en la consola.
        const errBody = await res.text();
        console.error("HubSpot: envío rechazado", res.status, errBody);
      }
    } catch (err) {
      console.error("HubSpot submit error", err);
    } finally {
      const dataLayer = window.dataLayer;
      if (dataLayer) {
        dataLayer.push({
          event: "calculadora_conversion",
          formFormat: "hubspot_whatsapp",
          productName: result.product.name,
          calculatedArea: `${result.baseArea.toFixed(2)} m²`,
          calculatedUnits: `${result.quantity} ${result.product.unit}`,
        });
      }
      window.open(whatsappUrl, "_blank");
      setSubmitting(false);
    }
  };

  return (
    <div className="deco-calc">
      {/* Hero — intro de la calculadora (el Header/Footer del sitio los pone la página) */}
      <section className="dc-hero">
        <div className="dc-badge">Calculadora de Material</div>
        <h1 className="dc-hero-title">
          Calcula lo justo. <br />
          <span className="dc-gold-text">Invierte con precisión.</span>
        </h1>
        <p className="dc-hero-sub">
          Estima al instante cuántas cajas, piezas o placas necesitas para tu proyecto, con el margen de merma que elijas.
        </p>
      </section>

      {/* Card */}
      <section className="dc-main">
        <div className="dc-card">
          <div className="dc-grid">
            {/* Izquierda: formulario */}
            <div className="dc-form">
              {/* Paso 01 */}
              <div className="dc-step-head">
                <div className="dc-step-num">Paso 01</div>
                <h2 className="dc-step-title">Selecciona tu producto</h2>
              </div>

              <div className="dc-cats" role="tablist" aria-label="Filtrar productos por categoría">
                {CATEGORY_FILTERS.map((cat) => {
                  const active = cat.id === categoryFilter;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => handleCategoryClick(cat.id)}
                      className={`dc-cat-btn ${active ? "is-active" : ""}`}
                    >
                      {cat.label}
                    </button>
                  );
                })}
              </div>

              <div key={categoryFilter} className="dc-products dc-fade-in">
                {filteredProducts.map((p) => {
                  const active = p.id === productId;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setProductId(p.id)}
                      className={`dc-product ${active ? "is-active" : ""}`}
                    >
                      <ProductThumb product={p} />
                      <div className="dc-product-name">{p.name}</div>
                    </button>
                  );
                })}
              </div>

              {/* Paso 02 */}
              <div className="dc-step-head dc-step-head--gap">
                <div className="dc-step-num">Paso 02</div>
                <h2 className="dc-step-title">
                  {product?.orientable ? "Mide tu muro" : "Ingresa tu área"}
                </h2>
              </div>

              {product?.orientable ? (
                /* Lambrines: ancho + altura del muro y sentido de instalación */
                <>
                  <div className="dc-dim-grid dc-inputs">
                    <div>
                      <label htmlFor="dc-wall-width" className="dc-label">
                        Ancho del muro
                      </label>
                      <div className="dc-input-wrap">
                        <input
                          id="dc-wall-width"
                          type="number"
                          inputMode="decimal"
                          min="0"
                          placeholder="Ej. 3.00"
                          value={wallWidth}
                          onChange={(e) => setWallWidth(e.target.value)}
                          className="dc-input"
                        />
                        <span className="dc-input-suffix">m</span>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="dc-wall-height" className="dc-label">
                        Altura del muro
                      </label>
                      <div className="dc-input-wrap">
                        <input
                          id="dc-wall-height"
                          type="number"
                          inputMode="decimal"
                          min="0"
                          placeholder="Ej. 2.40"
                          value={wallHeight}
                          onChange={(e) => setWallHeight(e.target.value)}
                          className="dc-input"
                        />
                        <span className="dc-input-suffix">m</span>
                      </div>
                    </div>
                  </div>

                  <p className="dc-step-desc" style={{ marginTop: "0.75rem" }}>
                    Instalación vertical (de piso a techo). Los recortes de este producto no se
                    reutilizan; por eso el cálculo usa las medidas del muro para estimar las piezas.
                  </p>
                </>
              ) : (
                /* Pisos y demás: área total o largo × ancho */
                <>
                  <div className="dc-toggle">
                    {["area", "dim"].map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setMode(m)}
                        className={`dc-toggle-btn ${mode === m ? "is-active" : ""}`}
                      >
                        {m === "area" ? "Área total (m²)" : "Largo × Ancho"}
                      </button>
                    ))}
                  </div>

                  <div className="dc-inputs">
                    {mode === "area" ? (
                      <div>
                        <label htmlFor="dc-area" className="dc-label">
                          Superficie total
                        </label>
                        <div className="dc-input-wrap">
                          <input
                            id="dc-area"
                            type="number"
                            inputMode="decimal"
                            min="0"
                            placeholder="Ej. 25"
                            value={area}
                            onChange={(e) => setArea(e.target.value)}
                            className="dc-input"
                          />
                          <span className="dc-input-suffix">m²</span>
                        </div>
                      </div>
                    ) : (
                      <div className="dc-dim-grid">
                        <div>
                          <label htmlFor="dc-length" className="dc-label">
                            Largo
                          </label>
                          <div className="dc-input-wrap">
                            <input
                              id="dc-length"
                              type="number"
                              inputMode="decimal"
                              min="0"
                              placeholder="0.0"
                              value={length}
                              onChange={(e) => setLength(e.target.value)}
                              className="dc-input"
                            />
                            <span className="dc-input-suffix">m</span>
                          </div>
                        </div>
                        <div>
                          <label htmlFor="dc-width" className="dc-label">
                            Ancho
                          </label>
                          <div className="dc-input-wrap">
                            <input
                              id="dc-width"
                              type="number"
                              inputMode="decimal"
                              min="0"
                              placeholder="0.0"
                              value={width}
                              onChange={(e) => setWidth(e.target.value)}
                              className="dc-input"
                            />
                            <span className="dc-input-suffix">m</span>
                          </div>
                        </div>
                        {computedArea > 0 && (
                          <div className="dc-area-calc">
                            Área calculada: <span className="dc-bold">{computedArea.toFixed(2)} m²</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Paso 03 */}
              <div className="dc-step-head dc-step-head--gap">
                <div className="dc-step-num">Paso 03</div>
                <h2 className="dc-step-title">Margen de desperdicio</h2>
                <p className="dc-step-desc">
                  Elige si deseas incluir un margen de seguridad para cortes y ajustes.
                </p>
              </div>

              <div className="dc-waste">
                {[
                  { id: "with", title: "Con Merma", sub: "+5% recomendado" },
                  { id: "without", title: "Sin Merma", sub: "Medida exacta" },
                ].map((opt) => {
                  const active = waste === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setWaste(opt.id)}
                      className={`dc-waste-btn ${active ? "is-active" : ""}`}
                    >
                      <span className={`dc-radio ${active ? "is-active" : ""}`}>
                        {active && <span className="dc-radio-dot" />}
                      </span>
                      <span>
                        <span className="dc-waste-title">{opt.title}</span>
                        <span className="dc-waste-sub">{opt.sub}</span>
                      </span>
                    </button>
                  );
                })}
              </div>

              {calcError && (
                <div role="alert" className="dc-calc-error">
                  {calcError}
                </div>
              )}

              <button type="button" onClick={handleCalculate} className="dc-calc-btn">
                Calcular Material
              </button>
            </div>

            {/* Derecha: resultado */}
            <div id="result-card" className="dc-result">
              <div className="dc-result-glow" />
              <div className="dc-result-inner">
                <div className="dc-result-eyebrow">Resultado</div>
                <h2 className="dc-result-title">Tu cálculo</h2>

                {!result ? (
                  <div className="dc-result-empty">
                    Selecciona un producto e ingresa la superficie de tu proyecto para ver el desglose recomendado.
                  </div>
                ) : (
                  <div className="dc-result-body">
                    {/* Producto seleccionado */}
                    <div className="dc-result-product">
                      <div className="dc-result-thumb">
                        <ProductThumb product={result.product} size="sm" />
                      </div>
                      <div className="dc-result-product-meta">
                        <div className="dc-result-product-eyebrow">Producto seleccionado</div>
                        <div className="dc-result-product-name">{result.product.name}</div>
                      </div>
                    </div>

                    {/* Cantidad — respuesta principal */}
                    <div className="dc-qty">
                      <div className="dc-qty-label">Cantidad a comprar</div>
                      <div className="dc-qty-value">
                        <span className="dc-qty-number">{result.quantity}</span>
                        <span className="dc-qty-unit">{result.product.unit}</span>
                      </div>
                    </div>

                    {/* Área — verificación secundaria */}
                    <div className="dc-area-box">
                      <div className="dc-area-box-label">{result.areaLabel}</div>
                      <div className="dc-area-box-value">
                        {result.areaValue.toFixed(2)} <span className="dc-area-box-unit">m²</span>
                      </div>
                      {result.isOrientable && (
                        <div className="dc-area-box-label" style={{ marginTop: "0.5rem" }}>
                          Muro: {result.wallW.toFixed(2)} × {result.wallH.toFixed(2)} m ({result.baseArea.toFixed(2)} m²) ·{" "}
                          {result.runs} columnas × {result.perRun} pza
                        </div>
                      )}
                    </div>

                    {/* Nota técnica */}
                    <div className="dc-note">
                      <div className="dc-note-label">Especificación técnica</div>
                      <p className="dc-note-text">
                        <svg className="dc-note-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 16v-4" />
                          <path d="M12 8h.01" />
                        </svg>
                        <span>{result.product.note}</span>
                      </p>
                    </div>

                    {/* Captura de lead */}
                    <div className="dc-lead">
                      <div className="dc-lead-title">
                        ¡Listo! Déjanos tu nombre y WhatsApp para enviarte tu cotización formal:
                      </div>
                      <input
                        type="text"
                        value={leadName}
                        onChange={(e) => setLeadName(e.target.value)}
                        placeholder="Tu nombre"
                        autoComplete="name"
                        className="dc-lead-input"
                      />
                      <input
                        type="tel"
                        inputMode="numeric"
                        value={leadPhone}
                        onChange={(e) => setLeadPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                        placeholder="Tu WhatsApp a 10 dígitos"
                        autoComplete="tel"
                        maxLength={10}
                        className="dc-lead-input"
                      />
                      {formError && (
                        <div role="alert" className="dc-lead-error">
                          {formError}
                        </div>
                      )}
                    </div>

                    {/* CTA WhatsApp */}
                    <button
                      type="button"
                      onClick={handleLeadSubmit}
                      disabled={submitting}
                      className="dc-wa-btn"
                    >
                      {submitting ? (
                        <>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="dc-spin">
                            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.3" strokeWidth="3" />
                            <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                          </svg>
                          Procesando...
                        </>
                      ) : (
                        <>
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M17.5 14.4c-.3-.1-1.7-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.7 1-.9 1.1-.2.2-.3.2-.6.1-.3-.1-1.2-.5-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6.1-.1.3-.3.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5 0-.1-.6-1.5-.9-2.1-.2-.5-.5-.5-.7-.5H8c-.2 0-.5.1-.7.3-.2.3-.9.9-.9 2.2 0 1.3.9 2.5 1.1 2.7.1.2 1.9 2.9 4.5 4 .6.3 1.1.4 1.5.5.6.2 1.2.2 1.7.1.5-.1 1.7-.7 1.9-1.3.2-.7.2-1.2.2-1.3-.1-.1-.3-.2-.6-.3zM12 2C6.5 2 2 6.5 2 12c0 1.8.5 3.5 1.3 5L2 22l5.2-1.3c1.4.8 3 1.2 4.8 1.2 5.5 0 10-4.5 10-10S17.5 2 12 2zm0 18.2c-1.6 0-3.1-.4-4.4-1.2l-.3-.2-3.2.8.8-3.1-.2-.3c-.9-1.4-1.3-3-1.3-4.6 0-4.5 3.7-8.2 8.2-8.2s8.2 3.7 8.2 8.2c0 4.5-3.6 8.2-8.2 8.2z" />
                          </svg>
                          Quiero cotizar ahora por WhatsApp
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <p className="dc-footnote">
          Puedes activar un 5% de merma recomendado para cortes y ajustes. Todas las cantidades se redondean a la unidad completa superior.
        </p>
      </section>
    </div>
  );
}

export default MaterialCalculator;